import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { InlineLoader } from '@/components/ui/InlineLoader';
import { StepIndicator } from '@/components/ui/StepIndicator';
import { BookDetailScreen } from '@/components/reading-tracker/BookDetailScreen';
import { BookGoalScreen } from '@/components/reading-tracker/BookGoalScreen';
import { SummaryScreen } from '@/components/reading-tracker/SummaryScreen';
import { ProgressScreen } from '@/components/reading-tracker/ProgressScreen';
import { launchConfetti } from '@/components/reading-tracker/confetti';
import { ENCOURAGEMENTS, STEPPER_BOUNDS } from '@/constants/reading-tracker';
import { booksService } from '@/services/books.service';
import { ApiError } from '@/services/http';
import { readingTrackerService } from '@/services/reading-tracker.service';
import { useSessionStore } from '@/store/session.store';
import {
  EMPTY_DRAFT,
  useReadingTrackerStore,
  type WizardScreen,
} from '@/store/reading-tracker.store';
import type { BookAccountMetadataView, BookView, ReadingGoalView } from '@/types/api';

/**
 * `/reader/:readerId/book/:bookId/tracker` — the wizard page.
 *
 * Mount sequence:
 *   1. Wait for the Auth0 token (so the API calls don't 401).
 *   2. In parallel: fetch the book + check for an active goal.
 *   3. If an active goal exists, jump to Screen 5 (`progress`).
 *   4. Otherwise start at Screen 1 (`book`) — or wherever the persisted
 *      wizard state left off if the user refreshed mid-flow.
 */
export function ReadingTrackerPage() {
  const { readerId, bookId } = useParams<{ readerId: string; bookId: string }>();
  const navigate = useNavigate();
  const accessToken = useSessionStore((s) => s.accessToken);

  // Wizard state (persisted to localStorage).
  const keyFor = useReadingTrackerStore((s) => s.keyFor);
  const setScreen = useReadingTrackerStore((s) => s.setScreen);
  const setDraft = useReadingTrackerStore((s) => s.setDraft);
  const setGoal = useReadingTrackerStore((s) => s.setGoal);
  const resetKey = useReadingTrackerStore((s) => s.resetKey);

  const key = readerId && bookId ? keyFor(readerId, bookId) : null;
  const screenFromStore = useReadingTrackerStore((s) =>
    key ? s.currentScreens[key] : undefined,
  );
  const draftFromStore = useReadingTrackerStore((s) =>
    key ? s.drafts[key] : undefined,
  );
  const goalFromStore = useReadingTrackerStore((s) =>
    key ? s.goalsByBook[key] : undefined,
  );

  const draft = draftFromStore ?? EMPTY_DRAFT;

  const [book, setBook] = useState<BookView | null>(null);
  // Per-account effective page/chapter counts (override ?? catalogue).
  const [accountMeta, setAccountMeta] = useState<BookAccountMetadataView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // ----- Bootstrap: fetch book + active goal in parallel ------------------
  useEffect(() => {
    if (!key || !readerId || !bookId) return;
    if (!accessToken) return;
    let cancelled = false;
    setBootstrapping(true);
    setLoadError(null);
    (async () => {
      try {
        const [bookView, activeGoal, meta] = await Promise.all([
          booksService.getById(bookId),
          readingTrackerService.active(readerId, bookId),
          // Soft-fail the account-metadata fetch so it never blocks the page;
          // the wizard falls back to the catalogue counts off the book.
          booksService.getAccountMetadata(bookId).catch((err) => {
            console.warn('[reading-tracker] account-metadata fetch failed:', err);
            return null;
          }),
        ]);
        if (cancelled) return;
        setBook(bookView);
        setAccountMeta(meta);
        setGoal(key, activeGoal);
        if (activeGoal) {
          setScreen(key, 'progress');
        } else if (!screenFromStore) {
          setScreen(key, 'book');
        }
      } catch (err) {
        if (cancelled) return;
        const status = err instanceof ApiError ? err.status : undefined;
        setLoadError(
          status === 404
            ? "We couldn't find that book."
            : err instanceof ApiError
              ? err.message
              : 'Could not load the reading tracker.',
        );
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // We intentionally exclude `screenFromStore` and `setScreen` /
    // `setGoal` — they're stable callbacks from Zustand, and we only
    // want this effect to re-run when the route or token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, readerId, bookId, accessToken]);

  const screen: WizardScreen = screenFromStore ?? 'book';

  // ----- Action handlers --------------------------------------------------
  const goTo = (next: WizardScreen) => {
    if (key) setScreen(key, next);
  };

  const handleStart = () => goTo('bookGoal');

  const handleGoalTypeChange = (type: typeof draft.goalType) => {
    if (!key || !type) return;
    setDraft(key, { goalType: type, perDay: STEPPER_BOUNDS[type].default });
  };

  const handlePerDayChange = (perDay: number) => {
    if (key) setDraft(key, { perDay });
  };

  const handleSaveCounts = async (patch: {
    pages?: number | null;
    chapters?: number | null;
  }) => {
    if (!bookId) return;
    const updated = await booksService.saveAccountMetadata(bookId, patch);
    setAccountMeta(updated);
  };

  const handleConfirmPlan = async () => {
    if (!key || !readerId || !bookId || !draft.goalType) return;
    setSubmitting(true);
    try {
      const created = await readingTrackerService.create({
        readerId,
        bookId,
        goalType: draft.goalType,
        perDay: draft.perDay,
      });
      setGoal(key, created);
      goTo('progress');
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Could not save your reading plan.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkDay = async (dayNumber: number) => {
    if (!key || !goalFromStore) return;
    try {
      const updated = await readingTrackerService.markDay(goalFromStore.id, dayNumber);
      setGoal(key, updated);
      // Random encouragement.
      const msg = ENCOURAGEMENTS[Math.floor(Math.random() * ENCOURAGEMENTS.length)];
      toast.success(msg);
      if (updated.status === 'completed') {
        // Wait 700ms so the dot fills, the bar animates, then confetti.
        window.setTimeout(() => {
          void launchConfetti();
        }, 700);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string; nextExpected?: number } | null;
        if (body?.error === 'OUT_OF_ORDER' && typeof body.nextExpected === 'number') {
          toast(`Complete Day ${body.nextExpected} first! 📅`, { duration: 3000 });
          return;
        }
      }
      toast.error(err instanceof ApiError ? err.message : 'Could not save that day.');
    }
  };

  const handleStartOver = async () => {
    if (!key || !goalFromStore) return;
    try {
      await readingTrackerService.abandon(goalFromStore.id);
    } catch (err) {
      // Even if abandon fails (already terminal), reset the local wizard
      // — the user clearly wants a fresh start. Worst case: a stale
      // goalsByBook entry that the next mount will reconcile.
      // eslint-disable-next-line no-console
      console.warn('[reading-tracker] abandon failed:', err);
    }
    resetKey(key);
    setScreen(key, 'bookGoal');
    setGoal(key, null);
  };

  const handleBackToLibrary = () => {
    navigate('/library');
  };

  // ----- Render -----------------------------------------------------------
  const stepNumber = useMemo(() => screenToStep(screen), [screen]);

  if (loadError) {
    return (
      <AppShell mode="kid">
        <div className="mx-auto max-w-md rounded-2xl border border-or-rose-100 bg-or-rose-50 p-6 text-center">
          <p className="text-[14px] text-or-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={handleBackToLibrary}
            className="mt-4 text-[13px] font-bold text-or-orange-600 hover:underline"
          >
            ← Back to library
          </button>
        </div>
      </AppShell>
    );
  }

  if (bootstrapping || !book || !key) {
    return (
      <AppShell mode="kid">
        <div className="flex min-h-[40vh] items-center justify-center">
          <InlineLoader label="Loading your reading plan…" />
        </div>
      </AppShell>
    );
  }

  // Effective counts for the wizard. Falls back to the catalogue counts off
  // the book when the account-metadata fetch hasn't landed or failed.
  const effectiveMeta: BookAccountMetadataView = accountMeta ?? {
    bookId: book.id,
    pages: book.pages,
    chapters: book.chapters,
    override: { pages: null, chapters: null },
    global: { pages: book.pages, chapters: book.chapters },
  };

  return (
    <AppShell mode="kid">
      <div className="mx-auto w-full max-w-[680px]">
        {screen !== 'book' ? (
          <div className="mb-5 flex justify-center">
            <StepIndicator current={stepNumber} total={3} />
          </div>
        ) : null}

        <motion.div
          key={screen}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {screen === 'book' && (
            <BookDetailScreen
              book={book}
              onStart={handleStart}
              onBack={handleBackToLibrary}
            />
          )}
          {screen === 'bookGoal' && (
            <BookGoalScreen
              accountMeta={effectiveMeta}
              goalType={draft.goalType}
              perDay={draft.perDay}
              onGoalTypeChange={handleGoalTypeChange}
              onPerDayChange={handlePerDayChange}
              onSubmit={() => goTo('summary')}
              onBack={() => goTo('book')}
              onSaveCounts={handleSaveCounts}
            />
          )}
          {screen === 'summary' && draft.goalType && (
            <SummaryScreen
              book={book}
              effectiveCounts={{ pages: effectiveMeta.pages, chapters: effectiveMeta.chapters }}
              goalType={draft.goalType}
              perDay={draft.perDay}
              onConfirm={handleConfirmPlan}
              onBack={() => goTo('bookGoal')}
              submitting={submitting}
            />
          )}
          {screen === 'progress' && goalFromStore && (
            <ProgressScreen
              goal={goalFromStore}
              onMarkDay={handleMarkDay}
              onStartOver={handleStartOver}
              onBackToBook={handleBackToLibrary}
            />
          )}
          {screen === 'progress' && !goalFromStore && (
            <div className="rounded-2xl border border-or-slate-200 bg-white p-6 text-center text-[14px] text-or-slate-600">
              No active goal yet — let&apos;s set one up.
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => goTo('book')}
                  className="text-[13px] font-bold text-or-orange-600 hover:underline"
                >
                  Start the wizard
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AppShell>
  );
}

function screenToStep(screen: WizardScreen): number {
  switch (screen) {
    case 'book':
      return 1;
    case 'bookGoal':
      return 1;
    case 'summary':
      return 2;
    case 'progress':
      return 3;
  }
}
